import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Film, RefreshCw, Play, AlertCircle, CheckCircle2, Loader2, Clock, Plus, Wand2, Scissors, Trash2 } from 'lucide-react';
import NewVideoProject from '../components/NewVideoProject';
import { StageBar, ScriptGate, ShotsGate, FootageGate } from '../components/VideoStages';
import NewFilmBrief from '../components/NewFilmBrief';
import AssembleGate from '../components/AssembleGate';
import FootageGrid from '../components/FootageGrid';
import ShotEditor from '../components/ShotEditor';
import FilmReview from '../components/FilmReview';
import MediaPicker from '../components/MediaPicker';
import { getCurrentUser } from '../services/auth';

/**
 * Marketing > Video Studio.
 *
 * Shows what the renderer decided and what it produced. Deliberately NOT a
 * timeline: the manifest is the source of truth and the point of this whole
 * subsystem is that nobody has to place clips by hand.
 *
 * ⚠️ IT POLLS THE LEDGER, NEVER THE HTTP RESPONSE. Queueing a render returns
 * 202 from a background function before any work starts, so "queued" is the
 * only thing that response can honestly say. Progress comes from re-reading
 * the job rows.
 *
 * ⚠️ EVERY NUMBER HERE IS MEASURED, NOT ESTIMATED. Render cost per second of
 * film is printed per variant rather than averaged, because 9:16 costs more
 * than twice what the 16:9 master does and an average hides the format that
 * will hit the 15-minute function ceiling first.
 */

const FN = '/.netlify/functions/video-studio';
const AUDIO_FN = '/.netlify/functions/video-audio-generate';
const POLL_MS = 8000;

async function post(url, action, params = {}) {
  const user = getCurrentUser();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(user?.accessToken ? { Authorization: `Bearer ${user.accessToken}` } : {}),
    },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${action} failed (${res.status})`);
  return data;
}
const api = (action, params) => post(FN, action, params);
const audioApi = (action, params) => post(AUDIO_FN, action, params);

/**
 * Choosing where inside a take a shot starts. This is the one editorial control
 * the planner cannot derive: it knows how LONG each shot holds, from the
 * narration, but which slice of a ten-second take to use is a judgement about
 * the picture and nothing in the audio can tell you.
 *
 * The slider is bounded by the footage - a shot holding 8s of a 10s clip can
 * start anywhere up to 2s in. Push past that and the render would hold a frozen
 * last frame rather than fail, which nobody notices until it is published.
 */
function InPoint({ shot, onSet, busy }) {
  const [val, setVal] = useState(shot.source_in || 0);
  useEffect(() => { setVal(shot.source_in || 0); }, [shot.source_in]);
  const max = Number(shot.max_in || 0);
  if (max <= 0.05) {
    return <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>uses the whole clip</span>;
  }
  const dirty = Math.abs(val - (shot.source_in || 0)) > 0.005;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input type="range" min={0} max={max} step={0.05} value={val}
        onChange={(e) => setVal(Number(e.target.value))} style={{ width: 120 }} />
      <span style={{ fontFamily: 'monospace', fontSize: 11, minWidth: 74 }}>
        {val.toFixed(2)}s / {max.toFixed(2)}s
      </span>
      {dirty ? (
        <button className="btn btn-primary" disabled={busy}
          onClick={() => onSet(shot.number, val)} style={{ padding: '2px 8px', fontSize: 11 }}>Set</button>
      ) : null}
    </div>
  );
}

const fmtDur = (s) => {
  const n = Number(s || 0);
  if (!n) return '-';
  const m = Math.floor(n / 60);
  const sec = (n % 60).toFixed(n < 60 ? 2 : 0);
  return m ? `${m}m ${String(sec).padStart(2, '0')}s` : `${sec}s`;
};
const fmtMb = (b) => (b ? `${(Number(b) / 1048576).toFixed(1)} MB` : '-');

const STATUS_STYLE = {
  done: { bg: '#e8f5ec', fg: '#1a7f37', label: 'Rendered' },
  queued: { bg: '#fff4e5', fg: '#b25e09', label: 'Queued' },
  running: { bg: '#e7f0f8', fg: 'var(--navy)', label: 'Rendering' },
  failed: { bg: '#fdecec', fg: '#c0392b', label: 'Failed' },
  failed_checks: { bg: '#fdecec', fg: '#c0392b', label: 'Failed its checks' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: 'var(--gray-100)', fg: 'var(--gray-500)', label: status };
  const Icon = status === 'done' ? CheckCircle2
    : status === 'running' ? Loader2
      : status === 'queued' ? Clock : AlertCircle;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.fg,
      padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    }}>
      <Icon size={12} className={status === 'running' ? 'spin' : ''} />
      {s.label}
    </span>
  );
}

export default function VideoStudio() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);   // brief -> new film
  const [attaching, setAttaching] = useState(false); // footage -> this film
  // The shot whose clip is being chosen. Holding the whole shot rather than its
  // number lets the picker name the line the clip has to carry.
  const [pickingShot, setPickingShot] = useState(null);
  // The shot being corrected, and where a shot is being inserted before.
  const [editingShot, setEditingShot] = useState(null);
  const [insertingBefore, setInsertingBefore] = useState(null);
  // A photograph being turned into a clip for a shot, and the job doing it.
  const [photoForShot, setPhotoForShot] = useState(null);
  const [stillJob, setStillJob] = useState(null);
  // The last review of this film, and its per-shot findings.
  const [review, setReview] = useState({ review: null, findings: [] });
  // What a picked image should be handed to. Held as a function because the
  // picker serves three different jobs on this page and the caller decides.
  const [pickImageFor, setPickImageFor] = useState(null);
  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const d = await api('list');
      setProjects(d.projects || []);
      if (!selected && d.projects?.length) setSelected(d.projects[0].id);
    } catch (e) { setErr(e.message); }
  }, [selected]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const d = await api('get', { projectId: id });
      setDetail(d);
      setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);

  const loadReview = useCallback(async (id) => {
    if (!id) { setReview({ review: null, findings: [] }); return; }
    try { setReview(await api('review-status', { projectId: id })); }
    catch (e) { setReview({ review: null, findings: [] }); }
  }, []);
  useEffect(() => { loadReview(selected); }, [selected, loadReview]);

  // Polled separately from the project: a review writes its own ledger and the
  // project row does not change while it runs.
  useEffect(() => {
    const st = review && review.review && review.review.status;
    if (!['queued', 'running'].includes(st)) return undefined;
    const t = setInterval(() => loadReview(selected), 6000);
    return () => clearInterval(t);
  }, [review, selected, loadReview]);

  // A photograph takes a few seconds to become a clip. Polled separately because
  // it writes to its own ledger, and stopped the moment it settles.
  useEffect(() => {
    if (!stillJob || ['done', 'failed'].includes(stillJob.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api('still-job', { jobId: stillJob.id });
        if (r.status === 'done' || r.status === 'failed') {
          setStillJob((cur) => (cur ? { ...cur, status: r.status, error: r.last_error } : cur));
          await loadDetail(selected);
        }
      } catch (e) { /* keep waiting */ }
    }, 4000);
    return () => clearInterval(t);
  }, [stillJob, selected, loadDetail]);

  // Poll only while something is actually in flight - a finished project should
  // not keep hitting the database every eight seconds forever.
  useEffect(() => {
    const writing = ['scripting', 'shotting', 'planning', 'generating', 'assembling'].includes(detail?.project?.stage);
    // A generation run takes ten minutes; a slower tick is plenty and keeps
    // the page off the database.
    const inFlight = writing
      || (detail?.jobs || []).some((j) => j.status === 'queued' || j.status === 'running');
    clearInterval(pollRef.current);
    if (inFlight) pollRef.current = setInterval(() => loadDetail(selected), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [detail, selected, loadDetail]);

  const render = async (variantIds) => {
    setBusy(true);
    setErr(null);
    try {
      const d = await api('render', { projectId: selected, variantIds });
      await loadDetail(selected);
      if (!d.queued?.length) setErr('Nothing to queue - a render is already running.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const act = async (action, params = {}) => {
    setBusy(true);
    setErr(null);
    try {
      // Returned, not swallowed: some actions answer with something the screen
      // needs to show - the assemble action names the shots whose lines will be
      // folded into a neighbour, and that has to reach the gate.
      const d = await api(action, { projectId: selected, ...params });
      await loadDetail(selected);
      await loadList();
      return d;
    } catch (e) { setErr(e.message); return null; }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    // Says what will go and what will stay, because the footage does NOT go -
    // generated clips and finished renders are Media Library assets that may
    // already be in use elsewhere.
    const ok = window.confirm(
      `Delete "${p.title}"?

The project and its ${p.job_count || 0} render job${p.job_count === 1 ? '' : 's'} are removed.
`
      + 'Any footage or finished films stay in the Media Library.');
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      const d = await api('delete', { projectId: p.id });
      if (selected === p.id) { setSelected(null); setDetail(null); }
      await loadList();
      if (d.assetsKept) setErr(`Deleted "${d.title}". ${d.assetsKept} clip${d.assetsKept === 1 ? '' : 's'} stayed in the Media Library, tagged ${d.slug}.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const setInPoint = async (shotNumber, sourceIn) => {
    setBusy(true);
    setErr(null);
    try {
      await api('set-in-point', { projectId: selected, shotNumber, sourceIn });
      await loadDetail(selected);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const jobs = detail?.jobs || [];
  const shots = detail?.shots || [];
  const project = detail?.project;
  const draft = detail?.draft || {};

  return (
    <div className="page-content">
      <style>{'.spin{animation:cpxspin 1s linear infinite}@keyframes cpxspin{to{transform:rotate(360deg)}}'}</style>

      <div className="breadcrumb"><a href="#marketing">Marketing</a><span> / </span><span>Video Studio</span></div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 18px' }}>
        <Film size={22} color="var(--orange)" />
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--navy)' }}>Video Studio</h1>
      </div>

      {err ? (
        <div style={{ background: '#fdecec', color: '#c0392b', padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
          {err}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Projects */}
        <div className="card" style={{ flex: '0 0 260px', minWidth: 240 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Projects</span>
            <button className="btn btn-primary" style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => setCreating(true)}>
              <Plus size={12} style={{ verticalAlign: -2, marginRight: 3 }} />New
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {!projects.length ? (
              <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-500)' }}>No projects yet.</div>
            ) : projects.map((p) => (
              <div
                key={p.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                  background: p.id === selected ? '#eef4f9' : '#fff',
                  borderLeft: `3px solid ${p.id === selected ? 'var(--orange)' : 'transparent'}`,
                  borderBottom: '1px solid var(--gray-200)',
                }}
              >
                <button
                  onClick={() => setSelected(p.id)}
                  style={{
                    flex: 1, minWidth: 0, textAlign: 'left', border: 'none', cursor: 'pointer',
                    background: 'transparent', padding: '11px 4px 11px 14px',
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 13 }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>
                    {p.shot_count} shots · {p.done_count}/{p.job_count} rendered
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{p.brand}</div>
                </button>
                <button
                  title="Delete this film"
                  disabled={busy}
                  onClick={() => remove(p)}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    padding: '11px 10px 0 0', color: 'var(--gray-500)', lineHeight: 1,
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div style={{ flex: 1, minWidth: 460, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {project ? (
            <>
              <StageBar stage={project.stage || 'planned'} />

              {project.stage_error ? (
                <div style={{ background: '#fdecec', color: '#c0392b', padding: '9px 12px', borderRadius: 6, fontSize: 12 }}>
                  {project.stage_error}
                </div>
              ) : null}

              {project.stage === 'scripting' || project.stage === 'shotting' ? (
                <div className="card"><div className="card-body" style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                  {project.stage === 'scripting' ? 'Writing the script…' : 'Writing the shot list…'} This takes
                  a few seconds and the page will catch up on its own.
                </div></div>
              ) : null}

              {project.stage === 'script_ready' ? (
                <ScriptGate draft={draft} busy={busy}
                  onApprove={(script) => act('approve-script', { script }).then(() => act('write-shots'))}
                  onRewrite={(notes) => act('write-script', { notes })} />
              ) : null}

              {project.stage === 'shots_ready' ? (
                <ShotsGate draft={draft} busy={busy}
                  onApprove={() => act('approve-shots')}
                  onRewrite={() => act('write-shots')} />
              ) : null}

              {['footage', 'generating', 'generating_failed'].includes(project.stage) ? (
                <FootageGate draft={draft} busy={busy} progress={detail?.progress}
                  takes={detail?.takes || []}
                  onGenerate={(provider) => act('generate-footage', { provider })}
                  onOpenAssemble={() => setAttaching(true)}>
                  <FootageGrid footage={detail?.footage || []} busy={busy}
                    onPlay={(f) => setPreview({ r2_key: f.key })}
                    onChoose={(f) => setPickingShot(f)}
                    onEdit={(f) => setEditingShot(f)}
                    onUsePhoto={(f) => setPhotoForShot(f)}
                    onInsertBefore={(f, where) => setInsertingBefore({ ...f, where })}
                    onRevert={(f) => act('set-shot-clip', { shotNumber: f.number, assetId: null })} />
                </FootageGate>
              ) : null}

              {/* The last mile: narrate, cut to the voice, score, queue every format.
                  Offered as soon as there is footage to cut - a film does not need
                  every shot to be worth assembling, and the gate names what is
                  missing rather than refusing. */}
              {(detail?.jobs || []).some((j) => j.status === 'done') ? (
                <FilmReview
                  review={review.review} findings={review.findings} busy={busy}
                  onRun={async () => {
                    setBusy(true); setErr(null);
                    try {
                      await api('review', { projectId: selected });
                      await loadReview(selected);
                    } catch (e) { setErr(e.message); } finally { setBusy(false); }
                  }}
                  onFixShot={(n) => {
                    const f = (detail?.footage || []).find((x) => x.number === n);
                    if (f) setEditingShot(f);
                  }} />
              ) : null}

              {['assembling', 'assembled', 'assembling_failed'].includes(project.stage) ? (
                <div className="card"><div className="card-body">
                  <FootageGrid footage={detail?.footage || []} busy={busy}
                    onPlay={(f) => setPreview({ r2_key: f.key })}
                    onChoose={(f) => setPickingShot(f)}
                    onEdit={(f) => setEditingShot(f)}
                    onUsePhoto={(f) => setPhotoForShot(f)}
                    onInsertBefore={(f, where) => setInsertingBefore({ ...f, where })}
                    onRevert={(f) => act('set-shot-clip', { shotNumber: f.number, assetId: null })} />
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    Swapping a shot changes the edit, so re-cut the film afterwards.
                  </div>
                </div></div>
              ) : null}

              {['footage', 'generating_failed', 'assembling', 'assembled', 'assembling_failed'].includes(project.stage)
                && (detail?.footage || []).some((f) => f.kind !== 'none') ? (
                  <AssembleGate project={project} draft={draft} busy={busy}
                    script={project.script}
                    onAssemble={(opts) => act('assemble', opts)}
                    onRender={() => render(null)}
                    onDuplicate={async () => {
                      const label = window.prompt('Name this copy (it keeps the script, the shots and every clip, but is not cut yet)', 'alternate voice');
                      if (label === null) return;
                      const r = await act('duplicate', { label, suffix: label });
                      if (r && r.projectId) { await loadList(); setSelected(r.projectId); }
                    }} />
                ) : null}

              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{project.title}</span>
                  <button className="btn btn-primary" disabled={busy} onClick={() => render(null)}>
                    <RefreshCw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                    {busy ? 'Queueing...' : 'Render all'}
                  </button>
                </div>
                <div className="card-body">
                  {project.script ? (
                    <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gray-700)', margin: '0 0 4px' }}>
                      {project.script}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* The edit */}
              {shots.length ? <div className="card">
                <div className="card-header">The edit — {shots.length} shots cut to the narration</div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table className="product-table">
                    <thead>
                      <tr><th>Shot</th><th>At</th><th>Holds</th><th>Cut on</th><th>Starts in the take</th></tr>
                    </thead>
                    <tbody>
                      {shots.map((s) => (
                        <React.Fragment key={s.number}>
                        <tr>
                          <td>{s.number}</td>
                          <td style={{ fontFamily: 'monospace' }}>{Number(s.start).toFixed(2)}s</td>
                          <td style={{ fontFamily: 'monospace' }}>{Number(s.duration).toFixed(2)}s</td>
                          <td>
                            {/* A forced cut is one with no pause to land on. Those
                                are the ones a person should look at first, so they
                                are the only ones coloured. */}
                            <span style={{ color: s.cut_reason === 'forced' ? '#b25e09' : 'var(--gray-500)', fontSize: 12 }}>
                              {s.cut_reason === 'forced' ? 'mid-sentence (no pause available)'
                                : s.cut_reason === 'pause' ? 'a pause'
                                  : s.cut_reason === 'short-pause' ? 'a short pause' : s.cut_reason}
                            </span>
                          </td>
                          <td><InPoint shot={s} onSet={setInPoint} busy={busy} /></td>
                        </tr>
                        {s.script_text ? (
                          <tr>
                            <td colSpan={5} style={{ paddingTop: 0, fontSize: 12, color: 'var(--gray-500)', fontStyle: 'italic' }}>
                              &ldquo;{s.script_text}&rdquo;
                            </td>
                          </tr>
                        ) : null}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div> : null}

              {/* Deliverables */}
              {jobs.length ? <div className="card">
                <div className="card-header">Deliverables</div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table className="product-table">
                    <thead>
                      <tr>
                        <th>Format</th><th>Size</th><th>Length</th><th>File</th>
                        <th>Render cost</th><th>Status</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((j) => {
                        const rate = j.ffmpeg_ms && Number(j.duration_s)
                          ? (j.ffmpeg_ms / 1000) / Number(j.duration_s) : null;
                        const failed = (j.checks || []).filter((c) => !c.pass);
                        return (
                          <React.Fragment key={j.id}>
                            <tr>
                              <td style={{ fontWeight: 600 }}>{j.variant_id}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {j.width ? `${j.width}x${j.height}` : '-'}
                              </td>
                              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtDur(j.duration_s)}</td>
                              <td style={{ fontSize: 12 }}>{fmtMb(j.size_bytes)}</td>
                              <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                                {rate ? `${rate.toFixed(2)}s per second` : '-'}
                              </td>
                              <td><StatusPill status={j.status} /></td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {j.r2_key && j.status === 'done' ? (
                                  <button className="btn btn-ghost" onClick={() => setPreview(j)}>
                                    <Play size={12} style={{ marginRight: 4, verticalAlign: -2 }} />Watch
                                  </button>
                                ) : null}
                                <button className="btn btn-ghost" disabled={busy || j.status === 'running'}
                                  onClick={() => render([j.variant_id])}>Re-render</button>
                              </td>
                            </tr>
                            {j.last_error ? (
                              <tr><td colSpan={7} style={{ color: '#c0392b', fontSize: 12 }}>{j.last_error}</td></tr>
                            ) : null}
                            {failed.map((c) => (
                              <tr key={c.name}><td colSpan={7} style={{ color: '#c0392b', fontSize: 12 }}>
                                Check failed: {c.name} — {c.detail}
                              </td></tr>
                            ))}
                            {(j.warnings || []).map((w, i) => (
                              <tr key={i}><td colSpan={7} style={{ color: '#b25e09', fontSize: 12 }}>{w}</td></tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div> : null}
            </>
          ) : (
            <div className="card"><div className="card-body">Select a project.</div></div>
          )}
        </div>
      </div>

      {creating ? (
        <NewFilmBrief
          onClose={() => setCreating(false)}
          onStarted={async (id) => {
            setCreating(false);
            await loadList();
            setSelected(id);
          }}
        />
      ) : null}

      {pickingShot ? (
        <MediaPicker
          videosOnly
          onClose={() => setPickingShot(null)}
          onPick={async (asset) => {
            const shotNumber = pickingShot.number;
            setPickingShot(null);
            await act('set-shot-clip', { shotNumber, assetId: asset.id });
          }}
        />
      ) : null}

      {insertingBefore ? (
        <MediaPicker
          videosOnly
          onClose={() => setInsertingBefore(null)}
          onPick={async (asset) => {
            const where = insertingBefore.where === 'after' ? 'afterShot' : 'beforeShot';
            const n = insertingBefore.number;
            setInsertingBefore(null);
            const r = await act('insert-shot', { [where]: n, assetId: asset.id });
            // The inserted shot takes the first half of its neighbour's line, so
            // say what moved. Silently re-timing the narration would be the kind
            // of change somebody discovers in the finished film.
            if (r && r.newShotLine) {
              setErr(null);
              window.alert(
                `Shot ${r.inserted} added.

It is on screen for: "${r.newShotLine}"
`
                + `The shot after it now carries: "${r.nextShotLine}"

`
                + 'The script itself has not changed. Re-cut the film to hear it.');
            }
          }}
        />
      ) : null}

      {photoForShot ? (
        <MediaPicker
          imagesOnly
          onClose={() => setPhotoForShot(null)}
          onPick={async (asset) => {
            const shotNumber = photoForShot.number;
            setPhotoForShot(null);
            // ⚠️ A PHOTOGRAPH IS NOT FOOTAGE. It becomes a real clip with a slow
            // move on it first, because handing the renderer a still would give a
            // frozen frame that passes every check made on the finished film.
            const r = await api('still-to-clip', { projectId: selected, assetId: asset.id, shotNumber, seconds: 6, move: 'in' });
            if (r && r.jobId) setStillJob({ id: r.jobId, name: r.name, shot: shotNumber, status: 'queued' });
          }}
        />
      ) : null}

      {stillJob ? (
        <div style={{
          position: 'fixed', right: 18, bottom: 18, zIndex: 1200, maxWidth: 340,
          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6,
          boxShadow: '0 4px 14px rgba(0,0,0,.13)', padding: '11px 13px', fontSize: 12,
        }}>
          {stillJob.status === 'done' ? (
            <><strong style={{ color: '#1a7f37' }}>Shot {stillJob.shot} is now that photograph</strong>
              <div style={{ color: 'var(--gray-500)', marginTop: 4 }}>
                Six seconds with a slow push in. Re-cut the film to see it in place.
              </div></>
          ) : stillJob.status === 'failed' ? (
            <><strong style={{ color: '#c0392b' }}>That photograph could not be made into a clip</strong>
              <div style={{ color: 'var(--gray-500)', marginTop: 4 }}>{stillJob.error}</div></>
          ) : (
            <><Loader2 size={12} className="spin" style={{ verticalAlign: -2, marginRight: 6 }} />
              Making a clip from {stillJob.name}…</>
          )}
          <button className="btn btn-ghost" style={{ fontSize: 11, marginTop: 6, padding: '2px 8px' }}
            onClick={() => setStillJob(null)}>Close</button>
        </div>
      ) : null}

      {pickImageFor ? (
        <MediaPicker
          imagesOnly
          onClose={() => setPickImageFor(null)}
          onPick={(asset) => { pickImageFor(asset); setPickImageFor(null); }}
        />
      ) : null}

      {editingShot ? (
        <ShotEditor
          shot={editingShot}
          busy={busy}
          references={(detail?.referenceAssets || []).filter(
            (a) => (editingShot.referenceAssetIds || []).includes(a.id))}
          onClose={() => setEditingShot(null)}
          onPickReference={(cb) => setPickImageFor(() => cb)}
          onSave={async (patch) => {
            const shotNumber = editingShot.number;
            setEditingShot(null);
            await act('update-shot', { shotNumber, ...patch });
          }}
          onRedo={async ({ provider, ...patch }) => {
            const shotNumber = editingShot.number;
            setEditingShot(null);
            await act('update-shot', { shotNumber, ...patch });
            await act('generate-footage', { shotNumbers: [shotNumber], provider });
          }}
        />
      ) : null}

      {attaching ? (
        <NewVideoProject
          projectId={selected}
          shotCount={(draft.shots || []).length}
          onClose={() => setAttaching(false)}
          onCreated={async () => {
            setAttaching(false);
            await loadDetail(selected);
          }}
        />
      ) : null}

      {preview ? (
        <div onClick={() => setPreview(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <video
            onClick={(e) => e.stopPropagation()}
            src={`/.netlify/functions/r2-fetch?key=${encodeURIComponent(preview.r2_key)}`}
            controls autoPlay
            style={{ maxWidth: '100%', maxHeight: '92vh', background: '#000' }}
          />
        </div>
      ) : null}
    </div>
  );
}
